import { useState } from "react";
import { CalculationFilterStatus } from "../../../../config/enums/ProductType.enum";

const DEFAULT_CALCULATE_STATUS = CalculationFilterStatus.NONE;
const DEFAULT_ORDER_BY = "createdAtDesc";

// Same raw localStorage keys the legacy `ProductFilter` writes on reset/apply
// (see src/screens/product/productList/ProductFilter.tsx) — kept as-is so
// anything else in the app reading these keys keeps working unchanged.
const CALCULATE_STATUS_KEY = "calculateStatus";
const PRODUCT_ORDER_BY_KEY = "productOrderBy";

export interface ProductFilterState {
  calculateStatus: CalculationFilterStatus;
  orderBy: string;
  setCalculateStatus: (value: CalculationFilterStatus) => void;
  setOrderBy: (value: string) => void;
  reset: () => void;
  apply: () => void;
}

/**
 * Tailwind replacement for the state/localStorage-persist logic in the
 * legacy `ProductFilter` (see
 * src/screens/product/productList/ProductFilter.tsx). Same persisted keys,
 * same reset/apply semantics — the legacy filter never restored these from
 * localStorage on mount either, so this hook doesn't introduce that.
 */
export const useProductFilterState = (
  refetchWithFilters: (calculateStatus: CalculationFilterStatus, orderBy: string) => void
): ProductFilterState => {
  const [calculateStatus, setCalculateStatus] = useState<CalculationFilterStatus>(
    DEFAULT_CALCULATE_STATUS
  );
  const [orderBy, setOrderBy] = useState<string>(DEFAULT_ORDER_BY);

  const reset = () => {
    setCalculateStatus(DEFAULT_CALCULATE_STATUS);
    setOrderBy(DEFAULT_ORDER_BY);
    localStorage.setItem(CALCULATE_STATUS_KEY, DEFAULT_CALCULATE_STATUS);
    localStorage.setItem(PRODUCT_ORDER_BY_KEY, DEFAULT_ORDER_BY);
    refetchWithFilters(DEFAULT_CALCULATE_STATUS, DEFAULT_ORDER_BY);
  };

  const apply = () => {
    localStorage.setItem(CALCULATE_STATUS_KEY, calculateStatus);
    localStorage.setItem(PRODUCT_ORDER_BY_KEY, orderBy);
    refetchWithFilters(calculateStatus, orderBy);
  };

  return {
    calculateStatus,
    orderBy,
    setCalculateStatus,
    setOrderBy,
    reset,
    apply
  };
};
